import PropTypes from 'prop-types';
import { memo, useState, useEffect } from 'react';
import UITour from 'ui-shepherd-react/tour/UITour';
import MultiTourHandler from 'ui-shepherd-react/lib/MultiTourHandler';
import OnboardingTourSteps from './OnboardingTourSteps';
import { ONBOARDING_TOUR_SOURCE } from '../constants/URL';
import { OnboardingTourTrackingProvider } from './OnboardingTourTrackingProvider';
import { getTourById, getTourFromConfig } from '../util/getTour';
import { buildTourConfig } from '../util/tourConfig';
import { getLinkedTourState } from '../util/linkedTourUtils';
import { getTourIdFromQueryParams, getQueryParam, parseReturnUrl } from '../util/urlUtils';
import { localStorage } from '../util/storageUtil';
import { transformTour } from '../util/tourTransformer';
import { Metrics } from '../util/metrics';
import SentryManager from '../manager/SentryManager';
import { jsx as _jsx } from "react/jsx-runtime";
const SELENIUM_DISABLE_ONBOARDING_TOURS_KEY = 'selenium.disable.onboarding_tours';
const shouldDisableOnboardingTour = () => {
  return (
    // TODO: add link to selenium doc
    // Use the convention selenium.disable.<feature>.
    // selenium.disable.onboarding_tours = 'true' should prevent the tour from loading.
    localStorage.getItem(SELENIUM_DISABLE_ONBOARDING_TOURS_KEY) === 'true'
  );
};
const SELENIUM_ONBOARDING_TOUR_FLAG = 'selenium_onboarding_tour_enabled';
const SELENIUM_ONBOARDING_TOUR_CONFIG = 'selenium_onboarding_tour_config';
const setOnboardingTourConfigForSelenium = tourData => {
  if (localStorage.getItem(SELENIUM_ONBOARDING_TOUR_FLAG) === 'true') {
    localStorage.setItem(SELENIUM_ONBOARDING_TOUR_CONFIG, JSON.stringify(tourData));
  }
};
const defaultMultiTourHandler = new MultiTourHandler();
function OnboardingTour(props) {
  const {
    tourId: passedTourId,
    tour: localTour,
    multiTourHandler = defaultMultiTourHandler,
    navigationCallback
  } = props;
  const [activeTourId, setActiveTourId] = useState(null);
  const [tourState, setTourState] = useState({
    tourData: null,
    tourConfig: null
  });
  useEffect(() => {
    if (shouldDisableOnboardingTour()) {
      return;
    }
    const explicitTourId = passedTourId || getTourIdFromQueryParams();
    const linkedTourState = !explicitTourId ? getLinkedTourState() : null;
    const tourId = linkedTourState ? linkedTourState.tourId : explicitTourId;
    if (!tourId) {
      return;
    }
    let isLocalTour = false;
    const tourConfigPromise = localTour ? getTourFromConfig(localTour).then(tourData => {
      isLocalTour = true;
      return tourData;
    }) : getTourById(tourId);
    tourConfigPromise.then(tourData_ => {
      const tourData = tourData_;
      const id = tourId || tourData.alias;

      // If the tourId does not match the local tour alias, abort
      if (isLocalTour && tourId !== tourData.alias) {
        console.warn(`Skipping this tour because the provided tourId "${tourId}" doesn't match the local tour alias "${tourData.alias}".`);
        return;
      }
      setActiveTourId(id);
      tourData.steps = transformTour(tourData.steps);
      tourData.prevTourLength = (linkedTourState === null || linkedTourState === void 0 ? void 0 : linkedTourState.prevTourLength) || 0;
      tourData.linkedToursTotalStepCount = tourData.linkedToursTotalStepCount || (linkedTourState === null || linkedTourState === void 0 ? void 0 : linkedTourState.linkedToursTotalStepCount);
      tourData.returnUrl = (linkedTourState === null || linkedTourState === void 0 ? void 0 : linkedTourState.returnUrl) || parseReturnUrl(getQueryParam('returnUrl'));
      tourData.tourSource = (linkedTourState === null || linkedTourState === void 0 ? void 0 : linkedTourState.tourSource) || getQueryParam(ONBOARDING_TOUR_SOURCE) || undefined;
      const newTourConfig = buildTourConfig(id, tourData);
      setTourState({
        tourConfig: newTourConfig,
        tourData
      });
      setOnboardingTourConfigForSelenium(tourData);
    }).catch(error => {
      console.error(error);
      SentryManager.reportLoadTourError(error, tourId);
      Metrics.counter('load-onboarding-tour-error', {
        tourId: tourId
      }).increment();
    });
  }, [passedTourId, localTour]);
  const {
    tourConfig,
    tourData
  } = tourState;
  if (!tourData || !tourConfig || !activeTourId) {
    return null;
  }
  return /*#__PURE__*/_jsx(OnboardingTourTrackingProvider, {
    children: /*#__PURE__*/_jsx(UITour
    // @ts-expect-error _rootNode is not typed
    // eslint-disable-next-line react/prop-types
    , {
      _rootNode: props._rootNode,
      className: "onboarding-tour-container",
      config: tourConfig,
      multiTourHandler: multiTourHandler,
      navigationCallback: navigationCallback,
      tour: activeTourId,
      children: /*#__PURE__*/_jsx(OnboardingTourSteps, {
        tourData: tourData,
        tourId: activeTourId
      })
    })
  });
}
OnboardingTour.propTypes = {
  tourId: PropTypes.string
};
export default /*#__PURE__*/memo(OnboardingTour);
export const WEBPACK_3_FORCE_MODULE_IMPORT = 1;